import {ref} from 'vue';

type UseSidebarResizeAnchor = 'left' | 'right';
type UseSidebarResizeOptions = {
  /**
   * The minimum width allowed for the sidebar
   * @default 200
   */
  minWidth?: number;
  /**
   * The maximum width allowed for the sidebar
   * @default 600
   */
  maxWidth?: number;
};

const DEFAULT_ANCHOR = 'left';
const DEFAULT_STARTING_WIDTH = 300;
const DEFAULT_MIN_WIDTH = 200;
const DEFAULT_MAX_WIDTH = 600;

export function useSidebarResize(
  anchor: UseSidebarResizeAnchor = DEFAULT_ANCHOR,
  startingWidth = DEFAULT_STARTING_WIDTH,
  options: UseSidebarResizeOptions = {},
) {
  options.minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH;
  options.maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH;

  const sidebarWidth = ref(startingWidth);

  let referenceX = 0;
  let referenceWidth = 0;
  const handleResize = function(e: MouseEvent): void {
    const widthDifference = anchor === 'right' ? referenceX - e.clientX : e.clientX - referenceX;
    const resizedWidth = referenceWidth + widthDifference;
    sidebarWidth.value = Math.min(options.maxWidth, Math.max(options.minWidth, resizedWidth));
  };

  const handleStopResize = function(): void {
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', handleStopResize);

    // you may now select things again
    document.body.style.userSelect = '';

    referenceX = 0;
    referenceWidth = 0;
  };

  const handleStartResize = function(e: MouseEvent): void {
    // don't select things while we're dragging
    document.body.style.userSelect = 'none';

    referenceX = e.clientX;
    referenceWidth = sidebarWidth.value;

    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleStopResize);
  };

  return {sidebarWidth, handleStartResize};
}
